import React, { useEffect, useRef, useState, useCallback } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import { usePlaceSearch } from '../../hooks/usePlaceSearch';
import { useNaturalLanguageSearch } from '../../hooks/useNaturalLanguageSearch';
import { useNlSearchIdleSuggestions } from '../../hooks/useNlSearchIdleSuggestions';
import PlaceSearchResults from '../PlaceSearchResults/PlaceSearchResults';
import ParsedSearchSummary from './ParsedSearchSummary';
import FilterPanel from '../FilterPanel/FilterPanel';
import {
  searchFiltersRowStyle,
  searchFieldWrapperStyle,
  searchFieldContainerStyle,
  searchIconStyle,
  searchInputInnerStyle,
  searchClearButtonStyle,
  searchDropdownStyle,
  filterButtonStyle,
  filterIconStyle,
  filterBadgeStyle,
  resultsButtonPositionStyle,
  mobileSearchBarContainerStyle,
  mobileSearchBarContainerWithFiltersStyle,
  headerCenterSlotStyle,
  searchPlaceholderCss,
  dropdownKeyframes,
} from './MapHeaderSearchFilters.styles';
import { filterAccentCssVars } from '../FilterPanel/FilterPanel.styles';
import { buildLotViewQuotaExceededMessage } from '../../utils/lotViewQuota';
import { resolveParsedSearchActions } from '../../utils/resolveParsedSearchActions';
import { isNlSearchMock } from '../../../services/nlSearchService';
import { pickNlSearchPlaceholderExample, NL_SEARCH_SUGGESTED_QUERIES, NL_SEARCH_SHARED_SUGGESTED_QUERIES, NL_SEARCH_SHARED_PLACEHOLDER_EXAMPLES, NL_SEARCH_CAD_PLACEHOLDER_EXAMPLES, NL_SEARCH_CAD_SUGGESTED_QUERIES, NL_SEARCH_SHARED_CONTEXT, NL_SEARCH_CAD_CONTEXT, NL_SEARCH_RECENT_SCOPE_MAP, NL_SEARCH_RECENT_SCOPE_SHARED, NL_SEARCH_RECENT_SCOPE_CAD, NL_SEARCH_MAX_CHARS, NL_SEARCH_LOGGED_OUT_PLACEHOLDER, NL_SEARCH_QUOTA_EXCEEDED_CODE } from '../../data/nlSearchConstants';
import { looksLikeNlSearchQuery } from '../../utils/looksLikeNlSearchQuery';
import { toast } from '../../../Components/Toast';

/**
 * Barra de búsqueda y botón de filtros para el header del mapa.
 *
 * `parseSearchEnabled`: Enter manda la frase a `POST /parsesearchquery` (o al mock).
 * `parseSearchContext`: `shared-list` pide schema de lista; `cad-list` el de
 * ficha CAD. Sin él, solo Lote. En CAD no hay Nominatim.
 * En share, cada Enter manda `listing_price_bands` (USD por default; ARS si
 * la frase es alquiler / pesos). `listing_currency` solo si la frase nombra
 * moneda o el usuario tocó el chip — no la que dejó el parse anterior.
 * `onApplyParsedLotFilters(filters, { replaceListing })`: `replaceListing` solo
 * con `shared-list-v1`; el mapa país ignora el segundo arg.
 * `resolveViewProvince`: provincia visible para `view_province` (homónimos).
 * Elegir un resultado Nominatim del dropdown (click o flecha + Enter) no parsea
 * y no toca filtros. Enter sin ítem de teclado espera el parse: `location.bbox`
 * → `fitBounds` (sin Nominatim); si no, Nominatim solo con `location.query`
 * (sin recortar ni reintentar más corto; `[]` = cámara quieta). El dropdown
 * tipado no interviene en Enter. Si `lotViewQuotaExceeded`, la barra se
 * bloquea igual que Filtros.
 */
function MapHeaderSearchFilters({
  isLoggedIn,
  onFlyTo,
  showSearchControl = true,
  showFilterControl = true,
  activeFilters,
  activeFilterCount = 0,
  showOnlyFiltered,
  onSetFilterValue,
  onRemoveFilterChip,
  onToggleShowOnlyFiltered,
  onClearAllFilters,
  onOpenFilterResultsList = null,
  isFilterResultsOpen = false,
  filterScope = null,
  filterScopeLists = [],
  filterScopeListsLoading = false,
  filterScopeLoading = false,
  filterScopeError = null,
  filterScopeCount = 0,
  filterScopeMatchCount = null,
  onSetFilterScope = null,
  onFilterScopeDropdownOpen = null,
  filterScopeLocked = false,
  filterScopeLockedHint = null,
  mapFilterOptions = null,
  onRequestMapFilterOptions = null,
  isMobile = false,
  requiresAuth = true,
  bottomOffset = 0,
  accentColor = null,
  showListingFilters = false,
  showOfficialUnitFilters = false,
  showOfficialUnitCoverageNote = false,
  officialUnitFilter = null,
  officialUnitZoomLocked = false,
  lotViewQuotaExceeded = false,
  lotViewQuotaResetsAt = null,
  parseSearchEnabled = false,
  parseSearchContext = null,
  resolveParseSearchPricePayload = null,
  onApplyParsedLotFilters = null,
  onLotViewQuotaInfo = null,
  resolveViewProvince = null,
  skipPlaceSearch = false,
  filterGroups = null,
  hideFilterTabs = false,
  hideShowOnlyFiltered = false,
  resolveAppliedChips = null,
}) {
  // En vistas compartidas el token ya autoriza el contenido: no se pide sesión.
  const searchDisabled = requiresAuth && !isLoggedIn;
  const searchBarLocked = searchDisabled || lotViewQuotaExceeded;
  const filtersDisabled = searchBarLocked;
  const quotaExceededMessage = buildLotViewQuotaExceededMessage(lotViewQuotaResetsAt);
  const filtersDisabledMessage = searchDisabled
    ? 'Inicia sesión para aplicar filtros'
    : quotaExceededMessage;
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterFocusGroupId, setFilterFocusGroupId] = useState(null);
  const [filterHovered, setFilterHovered] = useState(false);
  const [listHovered, setListHovered] = useState(false);

  const containerRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const filtersRowRef = useRef(null);
  const filterButtonRef = useRef(null);
  const inputRef = useRef(null);
  const keyboardNavRef = useRef(false);

  const {
    query,
    results,
    loading,
    error,
    hoveredIndex,
    setHoveredIndex,
    handleQueryChange,
    clearQuery,
    parseResult,
    minChars,
  } = usePlaceSearch({
    disabled: searchBarLocked,
    skipGeocode: (q) => (
      skipPlaceSearch
      || (
        parseSearchEnabled
        && !searchBarLocked
        && looksLikeNlSearchQuery(q)
      )
    ),
  });

  const canParseSearch = parseSearchEnabled && !searchBarLocked;
  const isSharedListParse = parseSearchContext === NL_SEARCH_SHARED_CONTEXT;
  const isCadListParse = parseSearchContext === NL_SEARCH_CAD_CONTEXT;
  const nlParseContext = isSharedListParse
    ? NL_SEARCH_SHARED_CONTEXT
    : (isCadListParse ? NL_SEARCH_CAD_CONTEXT : null);
  const nlSuggestedQueries = isCadListParse
    ? NL_SEARCH_CAD_SUGGESTED_QUERIES
    : (isSharedListParse
      ? NL_SEARCH_SHARED_SUGGESTED_QUERIES
      : NL_SEARCH_SUGGESTED_QUERIES);
  const {
    loading: parseLoading,
    error: parseError,
    errorCode: parseErrorCode,
    result: parseResultData,
    parse: parseQuery,
    cancel: cancelParse,
    clearResult: clearParseResult,
    minChars: parseMinChars,
  } = useNaturalLanguageSearch({
    enabled: canParseSearch,
    context: nlParseContext,
    onQuotaInfo: onLotViewQuotaInfo,
  });

  const {
    recentQueries: idleRecentQueries,
    suggestedQueries: idleSuggestedQueries,
    rememberQuery: rememberNlQuery,
    forgetQuery: forgetNlQuery,
  } = useNlSearchIdleSuggestions({
    enabled: canParseSearch,
    suggestedQueries: nlSuggestedQueries,
    scope: isCadListParse
      ? NL_SEARCH_RECENT_SCOPE_CAD
      : (isSharedListParse ? NL_SEARCH_RECENT_SCOPE_SHARED : NL_SEARCH_RECENT_SCOPE_MAP),
  });

  const nlModeTyping = canParseSearch && looksLikeNlSearchQuery(query);
  const nlLeadActive = (parseSearchEnabled && (searchDisabled || lotViewQuotaExceeded))
    || (canParseSearch && (
      query.trim().length === 0 || nlModeTyping || parseLoading
    ));
  const inputLocked = searchBarLocked || parseLoading;

  const showParseSummary = canParseSearch && (parseLoading || parseError || parseResultData);
  const showSearchDropdown = showSearchControl && !searchBarLocked && searchDropdownOpen && (
    searchFocused || query.length > 0 || showParseSummary
  );

  const [nlPlaceholder] = useState(() => pickNlSearchPlaceholderExample(
    isCadListParse
      ? NL_SEARCH_CAD_PLACEHOLDER_EXAMPLES
      : (isSharedListParse
        ? NL_SEARCH_SHARED_PLACEHOLDER_EXAMPLES
        : undefined),
  ));
  const searchPlaceholder = lotViewQuotaExceeded
    ? quotaExceededMessage
    : searchDisabled
      ? (parseSearchEnabled
        ? NL_SEARCH_LOGGED_OUT_PLACEHOLDER
        : 'Iniciá sesión para buscar')
      : canParseSearch
        ? nlPlaceholder
        : 'Buscar por dirección, ciudad o localidad...';
  const SearchLeadIcon = nlLeadActive ? AutoAwesomeIcon : SearchIcon;

  const handleCancelParse = useCallback(() => {
    cancelParse();
    inputRef.current?.focus();
  }, [cancelParse]);

  const closeSearchDropdown = useCallback(() => {
    setSearchDropdownOpen(false);
    setSearchFocused(false);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    cancelParse();
    clearQuery();
    clearParseResult();
    closeSearchDropdown();
    onClearAllFilters?.();
  }, [cancelParse, clearQuery, clearParseResult, closeSearchDropdown, onClearAllFilters]);

  const handleSelectResult = useCallback((result) => {
    keyboardNavRef.current = false;
    const parsed = parseResult(result);
    onFlyTo?.(parsed);
    clearParseResult();
    closeSearchDropdown();
    inputRef.current?.blur();
  }, [onFlyTo, parseResult, clearParseResult, closeSearchDropdown]);

  const handleQueryInput = (value) => {
    keyboardNavRef.current = false;
    handleQueryChange(String(value || '').slice(0, NL_SEARCH_MAX_CHARS));
    clearParseResult();
  };

  const flyToFirstPlaceResult = () => {
    if (hoveredIndex >= 0 && results[hoveredIndex]) {
      handleSelectResult(results[hoveredIndex]);
      return true;
    }
    if (results[0]) {
      handleSelectResult(results[0]);
      return true;
    }
    return false;
  };

  const submitParsedSearch = async (rawQuery = query) => {
    if (!canParseSearch) return;
    const text = String(rawQuery ?? '').trim();
    if (text.length < parseMinChars) return;

    if (text !== query) handleQueryInput(text);

    setSearchDropdownOpen(true);
    setFilterPanelOpen(false);

    const pricePayload = isSharedListParse && typeof resolveParseSearchPricePayload === 'function'
      ? resolveParseSearchPricePayload(text)
      : null;

    const viewProvince = !isCadListParse && typeof resolveViewProvince === 'function'
      ? resolveViewProvince()
      : null;

    const parsed = await parseQuery(text, {
      ...(pricePayload
        ? {
          listingCurrency: pricePayload.listingCurrency,
          listingPriceBands: pricePayload.listingPriceBands,
        }
        : {}),
      ...(viewProvince ? { viewProvince } : {}),
    });
    if (!parsed?.data) {
      if (parsed?.error) {
        if (parsed.code === NL_SEARCH_QUOTA_EXCEEDED_CODE) toast.warning(parsed.error);
        else toast.error(parsed.error);
      }
      return;
    }
    const data = parsed.data;

    rememberNlQuery(text, data.intent);

    try {
      const { filtersToApply, replaceListing, flyTo, revealFiltered, fitFilteredLots, schemaBlocked } = await resolveParsedSearchActions(data, {
        parseContext: nlParseContext,
        listingPriceBandKeys: pricePayload?.listingPriceBands || [],
        sourceQuery: text,
      });

      if (schemaBlocked) {
        toast.warning('Esta interpretación usa un contrato que la app todavía no conoce.');
      }
      if (filtersToApply) {
        onApplyParsedLotFilters?.(filtersToApply, { replaceListing });
      }
      if (isCadListParse) {
        /* El loteo ya está encuadrado. Sin Nominatim. */
      } else if (flyTo) onFlyTo?.(flyTo);
      else if (fitFilteredLots) onFlyTo?.({ fitFilteredLots: true });
      else if (revealFiltered) onFlyTo?.({ revealFiltered: true });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error applying parsed search:', err);
      toast.error('No se pudo aplicar la búsqueda interpretada.');
    }
  };

  useEffect(() => {
    if (!lotViewQuotaExceeded) return undefined;
    cancelParse();
    closeSearchDropdown();
    return undefined;
  }, [lotViewQuotaExceeded, cancelParse, closeSearchDropdown]);

  const handleSearchKeyDown = (e) => {
    if (searchBarLocked || parseLoading) return;
    if (e.key === 'Escape') {
      closeSearchDropdown();
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!results.length) return;
      keyboardNavRef.current = true;
      setHoveredIndex((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!results.length) return;
      keyboardNavRef.current = true;
      setHoveredIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Flecha+Enter = ítem del dropdown (Nominatim). Enter sin nav de teclado
      // espera el parse; no vuela con el 1.er hit tipado mientras parsea.
      if (keyboardNavRef.current && hoveredIndex >= 0 && results[hoveredIndex]) {
        handleSelectResult(results[hoveredIndex]);
        return;
      }
      if (canParseSearch && query.trim().length >= parseMinChars) {
        submitParsedSearch();
        return;
      }
      if (!skipPlaceSearch) flyToFirstPlaceResult();
    }
  };

  const handleFilterToggle = () => {
    setFilterPanelOpen((prev) => !prev);
    setFilterFocusGroupId(null);
    closeSearchDropdown();
  };

  const openFiltersAt = useCallback((groupId) => {
    setFilterFocusGroupId(groupId || null);
    closeSearchDropdown();
    setFilterPanelOpen(true);
  }, [closeSearchDropdown]);

  const handleOpenResultsList = () => {
    closeSearchDropdown();
    setFilterPanelOpen(false);
    onOpenFilterResultsList?.();
  };

  // Cerrar dropdowns al click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeSearchDropdown();
        setFilterPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeSearchDropdown]);

  // Cerrar filtros si se abre búsqueda
  useEffect(() => {
    if (searchDropdownOpen) setFilterPanelOpen(false);
  }, [searchDropdownOpen]);

  if (!showSearchControl && !showFilterControl) return null;

  const searchBar = showSearchControl && (
    <div ref={searchWrapperRef} style={searchFieldWrapperStyle({ isMobile })} data-tour="searchbar">
      <div
        style={searchFieldContainerStyle({
          focused: searchFocused,
          disabled: searchBarLocked,
          hasValue: query.length > 0,
          parsing: parseLoading,
        })}
      >
        <SearchLeadIcon
          aria-hidden
          style={searchIconStyle({
            active: searchFocused || query.length > 0 || parseLoading,
            disabled: searchBarLocked,
            nlp: nlLeadActive,
            thinking: parseLoading,
          })}
        />
        <input
          ref={inputRef}
          type="text"
          className="map-header-search-input"
          value={query}
          onChange={(e) => handleQueryInput(e.target.value)}
          onFocus={() => {
            if (parseLoading || searchBarLocked) return;
            setSearchFocused(true);
            setSearchDropdownOpen(true);
          }}
          onBlur={() => setSearchFocused(false)}
          onKeyDown={handleSearchKeyDown}
          placeholder={searchPlaceholder}
          title={searchBarLocked ? searchPlaceholder : undefined}
          style={searchInputInnerStyle({ disabled: inputLocked })}
          autoComplete="off"
          spellCheck={false}
          maxLength={NL_SEARCH_MAX_CHARS}
          disabled={inputLocked}
          readOnly={parseLoading}
          aria-busy={parseLoading || undefined}
          aria-label={lotViewQuotaExceeded
            ? quotaExceededMessage
            : canParseSearch
              ? (skipPlaceSearch ? 'Describir lotes' : 'Buscar lugar o describir lotes')
              : 'Buscar lugar'}
        />
        {parseLoading ? (
          <button
            type="button"
            style={searchClearButtonStyle}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCancelParse}
            aria-label="Cancelar interpretación"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </button>
        ) : (
          query.length > 0 && !searchBarLocked && (
            <button
              type="button"
              style={searchClearButtonStyle}
              onClick={() => {
                clearQuery();
                clearParseResult();
                inputRef.current?.focus();
              }}
              aria-label="Limpiar búsqueda"
            >
              <CloseIcon style={{ fontSize: '18px' }} />
            </button>
          )
        )}
      </div>

      {showSearchDropdown && (
        <div style={searchDropdownStyle} role="listbox">
          {showParseSummary ? (
            <ParsedSearchSummary
              data={parseResultData}
              error={parseError}
              errorCode={parseErrorCode}
              loading={parseLoading}
              isMock={isNlSearchMock()}
              mapOptionsByField={mapFilterOptions}
              onOpenFilters={openFiltersAt}
              suggestedQueries={canParseSearch ? nlSuggestedQueries : []}
              onSelectQuery={canParseSearch ? submitParsedSearch : undefined}
            />
          ) : (
            <PlaceSearchResults
              results={results}
              loading={loading}
              error={error}
              query={query}
              minChars={minChars}
              hoveredIndex={hoveredIndex}
              onHoverIndexChange={(idx) => {
                if (keyboardNavRef.current && idx < 0) return;
                if (idx >= 0) keyboardNavRef.current = false;
                setHoveredIndex(idx);
              }}
              onSelectResult={handleSelectResult}
              disabled={searchDisabled}
              idleHint={canParseSearch
                ? (skipPlaceSearch
                  ? 'Describí lotes y presioná Enter'
                  : 'Buscá un lugar o describí lotes y presioná Enter')
                : 'Buscá una dirección o localidad en Argentina'}
              mixedSearch={canParseSearch}
              nlMode={nlModeTyping}
              suggestedQueries={canParseSearch ? idleSuggestedQueries : []}
              recentQueries={canParseSearch ? idleRecentQueries : []}
              onSelectQuery={canParseSearch ? submitParsedSearch : undefined}
              onForgetQuery={canParseSearch ? forgetNlQuery : undefined}
            />
          )}
        </div>
      )}
    </div>
  );

  const showListButton = showFilterControl
    && typeof onOpenFilterResultsList === 'function'
    && !filtersDisabled;

  const filterButton = showFilterControl && (
    <button
      ref={filterButtonRef}
      type="button"
      data-tour="filtros"
      onClick={handleFilterToggle}
      style={filterButtonStyle({ active: filterPanelOpen, hovered: filterHovered })}
      onMouseEnter={() => setFilterHovered(true)}
      onMouseLeave={() => setFilterHovered(false)}
      aria-expanded={filterPanelOpen}
      aria-label="Filtros de lotes"
    >
      <TuneIcon style={filterIconStyle({ active: filterPanelOpen })} />
      <span>Filtros</span>
      {!searchDisabled && activeFilterCount > 0 && (
        <span style={filterBadgeStyle}>{activeFilterCount}</span>
      )}
    </button>
  );

  const listButton = showListButton && (
    <button
      type="button"
      onClick={handleOpenResultsList}
      style={filterButtonStyle({ active: isFilterResultsOpen, hovered: listHovered })}
      onMouseEnter={() => setListHovered(true)}
      onMouseLeave={() => setListHovered(false)}
      aria-pressed={isFilterResultsOpen}
      aria-label="Ver resultados en lista"
    >
      <FormatListBulletedOutlinedIcon style={filterIconStyle({ active: isFilterResultsOpen })} />
      <span>{isMobile ? 'Resultados' : 'Ver resultados'}</span>
    </button>
  );

  const content = (
    <>
      <div
        ref={filtersRowRef}
        style={{
          ...searchFiltersRowStyle({ isMobile }),
          ...(showSearchControl ? {} : { width: 'auto' }),
        }}
      >
        {searchBar}
        {filterButton}
        {listButton ? (
          <div style={resultsButtonPositionStyle({ isMobile })}>
            {listButton}
          </div>
        ) : null}
      </div>

      {showFilterControl && filterPanelOpen && (
        <FilterPanel
          key={filterFocusGroupId || 'filters'}
          isMobile={isMobile}
          anchorRef={filterButtonRef}
          activeFilters={activeFilters}
          activeCount={activeFilterCount}
          showOnlyFiltered={showOnlyFiltered}
          onSetFilterValue={onSetFilterValue}
          onRemoveFilterChip={onRemoveFilterChip}
          onToggleShowOnlyFiltered={onToggleShowOnlyFiltered}
          onClearAll={handleClearAllFilters}
          onOpenResultsList={onOpenFilterResultsList}
          onClose={() => {
            setFilterPanelOpen(false);
            setFilterFocusGroupId(null);
          }}
          disabled={filtersDisabled}
          disabledMessage={filtersDisabledMessage}
          scope={filterScope}
          scopeLists={filterScopeLists}
          scopeListsLoading={filterScopeListsLoading}
          scopeLoading={filterScopeLoading}
          scopeError={filterScopeError}
          scopeCount={filterScopeCount}
          scopeMatchCount={filterScopeMatchCount}
          onSetScope={onSetFilterScope}
          onScopeDropdownOpen={onFilterScopeDropdownOpen}
          scopeLocked={filterScopeLocked}
          scopeLockedHint={filterScopeLockedHint}
          mapOptionsByField={mapFilterOptions}
          onRequestMapOptions={onRequestMapFilterOptions}
          bottomOffset={bottomOffset}
          accentColor={accentColor}
          showListingFilters={showListingFilters}
          showOfficialUnitFilters={showOfficialUnitFilters}
          showOfficialUnitCoverageNote={showOfficialUnitCoverageNote}
          officialUnitFilter={officialUnitFilter}
          officialUnitZoomLocked={officialUnitZoomLocked}
          focusGroupId={filterFocusGroupId}
          filterGroups={filterGroups}
          hideTabs={hideFilterTabs}
          hideShowOnlyFiltered={hideShowOnlyFiltered}
          resolveAppliedChips={resolveAppliedChips}
        />
      )}
    </>
  );

  return (
    <>
      <style>{searchPlaceholderCss}{dropdownKeyframes}</style>

      <div
        ref={containerRef}
        style={{
          minWidth: 0,
          width: isMobile ? undefined : '100%',
          maxWidth: isMobile ? undefined : '100%',
          ...(showSearchControl ? undefined : { flexShrink: 0, width: 'auto' }),
          ...filterAccentCssVars(accentColor),
        }}
      >
        {isMobile ? (
          <div
            style={filterPanelOpen
              ? mobileSearchBarContainerWithFiltersStyle
              : mobileSearchBarContainerStyle}
          >
            {content}
          </div>
        ) : (
          <div style={showSearchControl ? headerCenterSlotStyle : undefined}>
            {content}
          </div>
        )}
      </div>
    </>
  );
}

export default MapHeaderSearchFilters;
